import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import usePersonScreening from "../hooks/usePersonScreening";
import ScreeningWarning from "./ScreeningWarning";
import usePawnerScreeningDetails from "../hooks/usePawnerScreeningDetails";
import "./Renew.css";
import useEscapeToGoBack from "../hooks/useEscapeToGoBack"

const FastRenew = () => {
  useEscapeToGoBack();
  const navigate = useNavigate();

  const [mmyy, setMmyy] = useState("");
  const [receiptNo, setReceiptNo] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [firstMonthDiscount, setFirstMonthDiscount] = useState(false);
  const [error, setError] = useState("");
  const [staffCode, setStaffCode] = useState("");
  const [grantDate, setGrantDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");

  // ⬇️ Refs for focusing / selecting
  const mmyyRef = useRef(null);
  const receiptRef = useRef(null);

  useEffect(() => {
    axios.get("https://www.lianfoong.com/api/staff/session", { withCredentials: true })
      .then(response => {
        if (response.data && response.data.staffCode) {
          setStaffCode(response.data.staffCode);
        }
      });

    // Set Grant Date (today)
    const today = new Date();
    const formattedGrantDate = formatDate(today);

    // Expiry Date (6 months - 1 day from today)
    const expiry = new Date();
    expiry.setMonth(expiry.getMonth() + 6);
    expiry.setDate(expiry.getDate() - 1);
    const formattedExpiryDate = formatDate(expiry);

    setGrantDate(formattedGrantDate);
    setExpiryDate(formattedExpiryDate);

    // ⬇️ Focus MMYY on mount
    requestAnimationFrame(() => {
      mmyyRef.current?.focus();
      mmyyRef.current?.select?.();
    });
  }, []);

  const pawnerScreeningDetails = usePawnerScreeningDetails(transaction?.receiptNo);
  const screening = usePersonScreening(pawnerScreeningDetails.customer?.name, pawnerScreeningDetails.customer?.dob, { enabled: !!transaction, context: JSON.stringify([transaction, mmyy, receiptNo, firstMonthDiscount, grantDate, expiryDate]) });

  const handleSubmit = async () => {
    if (!transaction) { alert("No transaction found!"); return; }
    if (!pawnerScreeningDetails.customer?.name && !pawnerScreeningDetails.error) {
      alert(pawnerScreeningDetails.error || "Pawner details are loading. Please wait before renewing."); return;
    }
    if (!(await screening.confirmBeforeProceed())) return;
    const enteredHandler = prompt("Please enter your handler code:");
    if (!enteredHandler) { alert("Handler code is required!"); return; }
    if (!transaction)    { alert("No transaction found!");    return; }

    // "dd-MMM-yyyy" -> "yyyy-MM-ddT00:00:00"
    const convertDateToISO = (dateStr) => {
      const [day, month, year] = dateStr.split("-");
      const monthNumber = new Date(`${month} 1, 2000`).getMonth() + 1;
      return `${year}-${String(monthNumber).padStart(2, "0")}-${day}T00:00:00`;
    };

    const newReceiptNo = `${mmyy}/${receiptNo}`;

    const payload = {
      activeRecord: true,
      item: transaction.item,
      weight: transaction.weight,
      amount: transaction.amount,
      valuationAmount: transaction.valuationAmount,
      noOfItem: transaction.noOfItem,
      oldReceiptNo: transaction.receiptNo,
      receiptNo: newReceiptNo,
      grantDate: convertDateToISO(grantDate),
      expiryDate: convertDateToISO(expiryDate),
      handler: enteredHandler,
      pawnerNirc: transaction.pawnerNirc,
      remark: transaction.remark || "Renewal",
    };

    try {
      const res = await axios.post(
        "https://www.lianfoong.com/api/pawn-transactions",
        payload,
        { responseType: "blob", withCredentials: true }
      );

      // Download returned PDF
      const blob = new Blob([res.data], { type: "application/pdf" });
      const cd = res.headers["content-disposition"] || "";
      const m  = cd.match(/filename=([^;]+)/i);
      const filename = m ? m[1].replace(/"/g, "") : `receipt_${mmyy}_${receiptNo}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);

      // Deactivate old ticket
      await axios.put(
        "https://www.lianfoong.com/api/pawn-transactions/deactivate-old-ticket",
        null,
        { params: { oldReceiptNo: transaction.receiptNo }, withCredentials: true }
      );

      alert("Renewal successful! PDF downloaded.");
      navigate("/dashboard");
    } catch (error) {
      console.error("Fast Renew failed:", error);
      alert("Failed to process renewal.");
      navigate("/dashboard");
    }
  };

  const handleSearch = async () => {
    try {
      const response = await axios.get(`https://www.lianfoong.com/api/pawn-transactions/search`, {
        params: { mmyy, receiptNo },
      });

      const foundTransaction = response.data;

      if (!foundTransaction.activeRecord) {
        setTransaction(null);
        setError("Ticket is inactive.");
      } else {
        setTransaction(foundTransaction);
        setError("");
      }
    } catch (err) {
      setTransaction(null);
      setError("Transaction not found!");
    }
  };

  // ---------- helpers ----------
  const toMoney = (n) => Number(n ?? 0).toFixed(2);

  // Optional: business date override
  const getBusinessDate = () => {
    const stored = localStorage.getItem("customDate");
    return stored ? new Date(stored) : new Date();
  };

  const extractYmd = (s) => {
    const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    return { y: +m[1], m: +m[2], d: +m[3] };
  };

  // By-date commenced months (min 1): +1 only if today's day > grant day
  const computeMonthsPassed = (grantDateStr, refDate = getBusinessDate()) => {
    const ymd = extractYmd(grantDateStr);
    if (!ymd) return 1;

    const g = new Date(ymd.y, ymd.m - 1, ymd.d);
    const t = new Date(refDate.getFullYear(), refDate.getMonth(), refDate.getDate());

    let months = (t.getFullYear() - g.getFullYear()) * 12 + (t.getMonth() - g.getMonth());
    if (t.getDate() > g.getDate()) months += 1;
    if (months < 1) months = 1;
    return months;
  };

  const calculateInterest = () => {
    if (!transaction) return "0.00";

    const principal = Number(transaction.amount || 0);
    const monthsPassed = computeMonthsPassed(transaction.grantDate);

    let totalRate = 0.015 * monthsPassed;
    if (firstMonthDiscount) totalRate -= 0.005;
    if (totalRate < 0) totalRate = 0;

    return toMoney(principal * totalRate);
  };

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-GB", { month: "short" });
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateDDMMYYYY = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // ⬇️ Key handlers for Enter behavior
  const onMmyyKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      receiptRef.current?.focus();
      receiptRef.current?.select?.();
    }
  };

  const onReceiptKeyDown = (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
    if (transaction) {
      handleSubmit();   // submit immediately when we already have the transaction
    } else {
      handleSearch();   // otherwise search first
    }
  }
};

  return (
    <div className="renew-container">
      <h2 className="title">Renew Pawn Ticket</h2>

      <div className="form-group">
        <label>Enter Month and Year (MMYY):</label>
        <input
          type="text"
          ref={mmyyRef}
          value={mmyy}
          onChange={(e) => setMmyy(e.target.value)}
          onKeyDown={onMmyyKeyDown}
        />
      </div>

      <div className="form-group">
        <label>Enter Receipt Number:</label>
        <input
          type="text"
          ref={receiptRef}
          value={receiptNo}
          onChange={(e) => setReceiptNo(e.target.value)}
          onKeyDown={onReceiptKeyDown}
        />
      </div>

      <button className="search-btn" onClick={handleSearch}>Search</button>

      {error && <p className="error-message">{error}</p>}

      {transaction && (
        <div className="transaction-details">
          <div className="details-grid">
            <p><strong>Items:</strong> {transaction.item}</p>
            <p><strong>Weight:</strong> {transaction.weight}</p>
            <p><strong>Amount:</strong> ${transaction.amount.toFixed(2)}</p>

            {/* ✅ Months Pass uses by-date commenced rule */}
            <p><strong>Months Pass:</strong> {computeMonthsPassed(transaction.grantDate)}</p>

            <p><strong>Grant Date:</strong> {formatDateDDMMYYYY(transaction.grantDate)}</p>
            <p><strong>Expiry Date:</strong> {formatDateDDMMYYYY(transaction.expiryDate)}</p>
          </div>

          <div className="checkbox-group">
            <input
              type="checkbox"
              id="discount"
              checked={firstMonthDiscount}
              onChange={(e) => setFirstMonthDiscount(e.target.checked)}
            />
            <label htmlFor="discount">First Month Interest Discount (-0.5%)</label>
          </div>

          <div className="details-grid">
            <p><strong>Interest:</strong> ${calculateInterest()}</p>
            <p><strong>Amount + Interest:</strong> {
              (() => {
                const interest = Number(calculateInterest());
                return toMoney(Number(transaction.amount || 0) + interest);
              })()
            }</p>
          </div>

          <div className="form-group">
            <label>Amount Collected:</label>
            <input type="text" />
          </div>

          <ScreeningWarning screening={screening} error={pawnerScreeningDetails.error} />
          <button className="next-btn" onClick={handleSubmit}>Submit</button>
        </div>
      )}
    </div>
  );
};

export default FastRenew;
